import { ReactNode } from 'react';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import { Autocomplete, AutocompleteRenderGroupParams, Chip, Divider, FormControl, FormLabel, List, Stack, TextField, Typography } from '@mui/material';

import { FieldGroupedOption } from '../types/FormField';

type TagsAutocompleteFieldValue = {
  group: string;
  label: string;
  value: string;
};

interface TagsAutocompleteFieldProps {
  label: string;
  disabled: boolean;
  value?: TagsAutocompleteFieldValue[];
  onChange: ControllerRenderProps['onChange'];
  error?: FieldError;
  name: string;
  readOnly?: boolean;
  groupedOptions: FieldGroupedOption[];
}

export const TagsAutocompleteField = ({ label, disabled, value = [], onChange, error, name, readOnly = false, groupedOptions }: TagsAutocompleteFieldProps) => {
  const renderAutocompleteGroup = (params: AutocompleteRenderGroupParams) => {
    // Check if we are iterating over the last group, in that case we don't show the divider
    const { children, key, group } = params as unknown as { children: ReactNode[]; key: string; group: string };
    const showDivider = options.length > parseInt(key) + children.length;
    return (
      <li key={key}>
        <Typography component={'p'} variant="caption" px={2} py={1}>
          {group}
        </Typography>
        <List sx={{ p: 0 }}>{children}</List>
        {showDivider && <Divider sx={{ mx: 0, my: 1 }} />}
      </li>
    );
  };

  const handleOptionDelete = (index: number) => {
    const newValue = value.slice();
    newValue.splice(index, 1);
    onChange(newValue);
  };

  const renderSelectedOptions = () => {
    return groupedOptions.map(({ group, options }) => {
      const optionsToRender = options
        .flatMap((option) => {
          // Grab index to be able to remove the option from the value later
          const index = value.findIndex((selectedOption) => option.value === selectedOption.value);
          return index !== undefined && index !== -1 ? [{ ...value[index], index }] : [];
        })
        .filter(Boolean);

      // If there are no selected options withing that group, we don't render the group at all
      if (optionsToRender.length === 0) return;

      return (
        <Stack spacing={2} key={group}>
          <Typography fontWeight="bold">{group}</Typography>
          <Stack direction="row" spacing={1}>
            {optionsToRender.map((option) => (
              <Chip key={option.index} label={option.label} sx={{ width: 'fit-content' }} onDelete={() => handleOptionDelete(option.index)} />
            ))}
          </Stack>
        </Stack>
      );
    });
  };

  const options: TagsAutocompleteFieldValue[] = groupedOptions.flatMap((groupedOption) =>
    groupedOption.options.map((option) => ({ group: groupedOption.group, label: option.label || option.value, value: option.value })),
  );

  return (
    <>
      <FormControl fullWidth size="small">
        <FormLabel id={`${name}-label`}>{label}</FormLabel>
        <Stack spacing={2}>
          <Autocomplete
            getOptionLabel={(option) => option.label}
            groupBy={(option) => option.group}
            getOptionDisabled={(option) => Boolean(value?.find((v) => v.label === option.label))}
            renderGroup={renderAutocompleteGroup}
            multiple
            disableClearable
            disabled={disabled}
            aria-labelledby={`${name}-label`}
            size="small"
            options={options}
            value={value}
            renderTags={() => null}
            renderInput={(params) => {
              return <TextField {...params} error={!!error} aria-invalid={!!error} placeholder="Select option" />;
            }}
            onChange={(_, v) => onChange(v)}
            readOnly={readOnly}
            slotProps={{ listbox: { sx: { p: 0 } } }}
          />

          {renderSelectedOptions()}
        </Stack>
      </FormControl>
      {error && (
        <Typography variant="caption" color="error">
          {error!.message as string}
        </Typography>
      )}
    </>
  );
};
